import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { config } from '../../config';
import styles from './Footer.module.css';

export const Footer: React.FC = () => {
  const { settings } = useSiteSettings();

  const isInternal = (href: string) => href.startsWith('/') && !href.includes('#');

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        {/* Brand + horizontal menu (matches the source-site footer layout) */}
        <Link to="/" className={styles.footerBrand}>
          {settings.logo ? (
            <img src={settings.logo.url} alt={settings.siteName} className={styles.footerLogo} />
          ) : (
            settings.siteName
          )}
        </Link>

        {settings.navigation.length > 0 && (
          <nav className={styles.footerNav} aria-label="Stopka">
            <Link to="/" className={styles.footerNavLink}>Strona główna</Link>
            {settings.navigation.map((item, i) => (
              isInternal(item.url) ? (
                <Link key={i} to={item.url} className={styles.footerNavLink}>{item.label}</Link>
              ) : (
                <a key={i} href={item.url} className={styles.footerNavLink}>{item.label}</a>
              )
            ))}
          </nav>
        )}

        <div className={styles.content}>
          <div className={styles.section}>
            <h3>{settings.siteName}</h3>
            {settings.address && <p>{settings.address}</p>}
            {settings.phone && (
              <p>
                <a href={`tel:${settings.phone.replace(/\s/g, '')}`} className={styles.link}>
                  {settings.phone}
                </a>
              </p>
            )}
            {settings.email && (
              <p>
                <a href={`mailto:${settings.email}`} className={styles.link}>
                  {settings.email}
                </a>
              </p>
            )}
          </div>

          {(settings.facebookUrl || settings.instagramUrl) && (
            <div className={styles.section}>
              <h3>Social</h3>
              <div className={styles.socialLinks}>
                {settings.facebookUrl && (
                  <a 
                    href={settings.facebookUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.socialLink}
                    aria-label="Facebook"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"></path>
                    </svg>
                  </a>
                )}
                {settings.instagramUrl && (
                  <a 
                    href={settings.instagramUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.socialLink}
                    aria-label="Instagram"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path>
                    </svg>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.bottom}>
          <p className={styles.copyright}>
            &copy; {new Date().getFullYear()} {settings.siteName}. Wszelkie prawa zastrzeżone.
          </p>
          {config.isDemo ? (
            <p className={styles.demoCredit}>
              Demo site by{' '}
              <a
                href="https://umowsie.com.pl"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.demoCreditLink}
              >
                UmówSię
              </a>
            </p>
          ) : (
            <p className={styles.poweredBy}>
              Powered by{' '}
              <a
                href="https://umowsie.com.pl"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.poweredByLink}
              >
                UmówSię
              </a>
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};
